import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import homeProductImage from '../../.stitch-parity/designs/home.png';
import Icon from '../components/Icon';
import { PLAY_STORE_URL } from '../lib/links';

gsap.registerPlugin(ScrollTrigger, useGSAP);

type Capability = {
  title: string;
  description: string;
  icon: string;
};

const capabilities: Capability[] = [
  {
    title: 'Voice input',
    description: 'Dictate text into fields from the Loaf Android keyboard.',
    icon: 'graphic_eq',
  },
  {
    title: 'AI writing',
    description: 'Rewrite, proofread, and compose text with AI assistance when available.',
    icon: 'edit_document',
  },
  {
    title: 'Synced history',
    description: 'Review cloud history when sync is enabled for your account.',
    icon: 'history',
  },
  {
    title: 'Snippets',
    description: 'Manage saved text expansions for use from your keyboard.',
    icon: 'text_snippet',
  },
];

const androidScreenshots = [
  { src: '/screenshots/1_keyboard.png', alt: 'Loaf Android keyboard — default typing layout', label: 'Keyboard' },
  { src: '/screenshots/2_ai_tools.png', alt: 'Loaf Android keyboard — AI writing tools', label: 'AI writing' },
  { src: '/screenshots/3_writing_style.png', alt: 'Loaf Android keyboard — rewrite with writing styles', label: 'Writing styles' },
  { src: '/screenshots/4_voice_dictation.png', alt: 'Loaf Android keyboard — live voice dictation', label: 'Voice dictation' },
  { src: '/screenshots/5_voice_preview.png', alt: 'Loaf Android keyboard — transcript review and preview', label: 'Voice preview' },
] as const;

function CapabilityAccordion() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-outline-variant/60 bg-surface sm:grid-cols-2 lg:grid-cols-4">
      {capabilities.map((capability, index) => {
        const isOpen = openIndex === index;
        const panelId = `capability-panel-${index}`;

        return (
          <div
            key={capability.title}
            className={`border-outline-variant/50 ${index < 3 ? 'border-b' : ''} ${index < 2 ? 'sm:border-b' : 'sm:border-b-0'} lg:border-b-0 ${index < 3 ? 'lg:border-r' : ''}`}
          >
            <button
              type="button"
              aria-controls={panelId}
              aria-expanded={isOpen}
              className="flex min-h-14 w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-surface-container focus-visible:relative"
              onClick={() => setOpenIndex(isOpen ? -1 : index)}
            >
              <span className="flex items-center gap-3">
                <Icon name={capability.icon} className="text-primary" />
                <span className="font-title-md text-title-md text-on-surface">{capability.title}</span>
              </span>
              <Icon name={isOpen ? 'remove' : 'add'} className="shrink-0 text-on-surface-variant" />
            </button>
            <div
              id={panelId}
              hidden={!isOpen}
              className="px-5 pb-5 font-body-sm text-body-sm leading-relaxed text-on-surface-variant"
            >
              {capability.description}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AndroidShowcase() {
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const count = androidScreenshots.length;

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isPaused || isHovered || isFocused) {
      return;
    }
    const id = window.setInterval(() => setActive((a) => (a + 1) % count), 5000);
    return () => window.clearInterval(id);
  }, [count, isPaused, isHovered, isFocused]);

  const goTo = (i: number) => setActive(((i % count) + count) % count);

  return (
    <section id="android" className="mx-auto max-w-max-width-content px-gutter py-20 sm:py-24">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-end lg:gap-16">
        <h2 className="max-w-xl font-headline-md text-headline-md text-on-surface">
          The keyboard that goes everywhere you type.
        </h2>
        <p className="max-w-[65ch] font-body-md text-body-md leading-relaxed text-on-surface-variant">
          Loaf lives in your Android keyboard. Dictate, rewrite, and compose from any text field — with optional cloud sync to review history and manage snippets in the web companion.
        </p>
      </div>

      <div
        className="mt-12 flex items-center justify-center gap-3 sm:gap-6"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocusCapture={() => setIsFocused(true)}
        onBlurCapture={() => setIsFocused(false)}
      >
        <button
          type="button"
          aria-label="Previous screenshot"
          onClick={() => goTo(active - 1)}
          className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full border border-outline-variant bg-surface text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
        >
          <Icon name="arrow_back" />
        </button>

        <div className="relative w-full max-w-[280px]">
          <div className="overflow-hidden rounded-[2rem] border-[8px] border-surface-container-high bg-surface shadow-2xl shadow-black/50">
            <div className="relative aspect-[9/16] overflow-hidden rounded-[1.5rem] bg-background">
              {androidScreenshots.map((shot, i) => (
                <img
                  key={shot.src}
                  src={shot.src}
                  alt={shot.alt}
                  width={1080}
                  height={1920}
                  aria-hidden={i !== active}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${i === active ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
                />
              ))}
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="Next screenshot"
          onClick={() => goTo(active + 1)}
          className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full border border-outline-variant bg-surface text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
        >
          <Icon name="arrow_forward" />
        </button>
      </div>

      <div className="mt-8 flex flex-col items-center gap-3">
        <div className="flex items-center gap-3">
          <span className="font-label-lg text-label-lg text-on-surface-variant">{androidScreenshots[active].label}</span>
          <button
            type="button"
            aria-label={isPaused ? 'Resume screenshot slideshow' : 'Pause screenshot slideshow'}
            onClick={() => setIsPaused((p) => !p)}
            className="inline-flex min-h-8 min-w-8 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:text-on-surface focus-visible:outline-2 focus-visible:outline-primary"
          >
            <Icon name={isPaused ? 'play_arrow' : 'pause'} className="text-[18px]" />
          </button>
        </div>
        <div className="flex items-center gap-1" role="tablist" aria-label="Screenshots">
          {androidScreenshots.map((shot, i) => (
            <button
              key={shot.src}
              type="button"
              role="tab"
              aria-label={`Show ${shot.label}`}
              aria-selected={i === active}
              onClick={() => goTo(i)}
              className="inline-flex min-h-11 min-w-8 items-center justify-center p-2 focus-visible:outline-none"
            >
              <span
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === active ? 'w-8 bg-primary' : 'w-2 bg-outline-variant hover:bg-on-surface-variant'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * LandingView — public product introduction at /.
 *
 * Leads with the Android keyboard (the primary surface) and frames the web
 * companion as the secondary surface. The hero pairs a phone showing the
 * keyboard in front of the desktop companion so visitors see both surfaces
 * at a glance, and the Android showcase carousel below cycles through the
 * store-listing screenshots.
 */
export default function LandingView() {
  const pageRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const words = gsap.utils.toArray<HTMLElement>('[data-reveal-word]');
      const desktop = pageRef.current?.querySelector<HTMLElement>('[data-product-desktop]');
      const phone = pageRef.current?.querySelector<HTMLElement>('[data-product-phone]');

      gsap.set(words, { opacity: 0, y: 18 });
      gsap.to(words, {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: 'power3.out',
        stagger: 0.045,
        scrollTrigger: {
          trigger: '[data-reveal-copy]',
          start: 'top 82%',
          end: 'bottom 48%',
          scrub: 0.8,
        },
      });

      if (desktop) {
        gsap.from(desktop, {
          opacity: 0,
          y: 30,
          duration: 0.9,
          ease: 'power3.out',
          delay: 0.2,
        });
      }

      if (phone) {
        gsap.from(phone, {
          opacity: 0,
          y: 50,
          scale: 0.92,
          duration: 1,
          ease: 'power3.out',
          delay: 0.45,
        });
      }
    },
    { scope: pageRef },
  );

  return (
    <div ref={pageRef} className="overflow-x-clip bg-background text-on-surface">
      <section className="mx-auto grid min-h-[100dvh] max-w-max-width-content items-center gap-12 px-gutter py-16 sm:py-20 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-16 lg:py-24">
        <div data-reveal-copy className="max-w-2xl">
          <h1 className="max-w-5xl font-headline-lg text-[clamp(2.85rem,4.8vw,5.25rem)] leading-[0.96] tracking-[-0.055em] text-on-surface">
            <span className="block">
              <span data-reveal-word className="inline-block">Start</span>{' '}
              <span data-reveal-word className="inline-block">with</span>{' '}
              <span data-reveal-word className="inline-block">voice.</span>
            </span>
            <span className="block text-on-surface-variant">
              <span data-reveal-word className="inline-block">Make</span>{' '}
              <span data-reveal-word className="inline-block">it</span>{' '}
              <span data-reveal-word className="inline-block">yours.</span>
            </span>
          </h1>
          <p className="mt-7 max-w-[65ch] font-body-lg text-body-lg leading-relaxed text-on-surface-variant">
            Loaf is an Android keyboard that turns your voice into text in any field — with a web companion to review history and manage snippets.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-primary px-7 font-title-md text-title-md text-on-primary transition-transform hover:brightness-105 active:scale-[0.98]"
            >
              <Icon name="android" className="text-[20px]" />
              <span>Get the app</span>
            </a>
            <Link
              to="/auth"
              className="inline-flex min-h-12 items-center justify-center rounded-lg border border-outline-variant bg-surface-container px-6 font-title-md text-title-md text-on-surface transition-colors hover:bg-surface-container-high active:scale-[0.98]"
            >
              Sign in to companion
            </Link>
          </div>
        </div>

        <div className="relative min-w-0">
          <div className="absolute -inset-8 rounded-[3rem] bg-primary/10 blur-3xl" aria-hidden="true" />

          {/* Desktop companion — behind, hidden on mobile */}
          <div
            data-product-desktop
            className="relative hidden overflow-hidden rounded-2xl border border-outline-variant/70 bg-surface p-2 shadow-2xl shadow-black/30 lg:block"
          >
            <div className="overflow-hidden rounded-xl bg-background">
              <img
                src={homeProductImage}
                alt="Loaf web companion home view showing recent activity and saved snippets"
                className="block h-auto w-full"
              />
            </div>
          </div>

          {/* Phone — front, overlapping on lg, centered on mobile */}
          <div
            data-product-phone
            className="relative mx-auto w-full max-w-[260px] lg:absolute lg:bottom-6 lg:right-0 lg:mx-0 lg:w-[42%] lg:max-w-[240px]"
          >
            <div className="overflow-hidden rounded-[1.75rem] border-[6px] border-surface-container-high bg-surface shadow-2xl shadow-black/50">
              <div className="relative aspect-[9/16] overflow-hidden rounded-[1.4rem] bg-background">
                <img
                  src="/screenshots/1_keyboard.png"
                  alt="Loaf Android keyboard showing the default typing layout"
                  width={1080}
                  height={1920}
                  loading="eager"
                  className="block h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="capabilities" className="mx-auto max-w-max-width-content px-gutter py-20 sm:py-24">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-end lg:gap-16">
          <div>
            <h2 className="max-w-xl font-headline-md text-headline-md text-on-surface">
              Review and shape what you start on Android.
            </h2>
          </div>
          <p className="max-w-[65ch] font-body-md text-body-md leading-relaxed text-on-surface-variant">
            Use the companion to review what you have dictated, shape text with AI assistance, and keep saved expansions close when cloud history is enabled.
          </p>
        </div>

        <div className="mt-12 grid grid-flow-dense grid-cols-1 border-y border-outline-variant/60 sm:grid-cols-2">
          {capabilities.map((capability, index) => (
            <article
              key={capability.title}
              className={`min-h-52 border-outline-variant/60 bg-surface p-6 sm:p-8 ${index % 2 === 0 ? 'sm:border-r' : ''} ${index < 3 ? 'border-b' : ''} ${index < 2 ? 'sm:border-b' : 'sm:border-b-0'}`}
            >
              <Icon name={capability.icon} className="text-primary" />
              <h3 className="mt-8 font-headline-md text-headline-md text-on-surface">{capability.title}</h3>
              <p className="mt-3 max-w-sm font-body-md text-body-md leading-relaxed text-on-surface-variant">
                {capability.description}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-16">
          <div className="mb-5">
            <h2 className="font-headline-md text-headline-md text-on-surface">Choose what to bring forward</h2>
          </div>
          <CapabilityAccordion />
        </div>
      </section>

      <AndroidShowcase />
    </div>
  );
}
